import { Injectable } from "@angular/core";
import "rxjs/add/operator/map";
import { ReplaySubject } from "rxjs/ReplaySubject";
import { Observable } from "rxjs/Observable";

// Classes.
import { CategoryListVD } from "../viewdata/category-list.vd";
import { CountryListVD } from "../viewdata/country-list.vd";
import { RecordListVD } from "../viewdata/record-list.vd";
import { WCupRankingCategoryListVD } from "../viewdata/wcup-ranking-category-list.vd";
import { WCupRankingListVD } from "../viewdata/wcup-ranking-list.vd";
import { WorldRankingCategoryListVD } from "../viewdata/world-ranking-category-list.vd";
import { WorldRankingListVD } from "../viewdata/world-ranking-list.vd";
import { AthleteListVD } from "../viewdata/athlete-list.vd";
import { AthleteVD } from "../viewdata/athlete.vd";
import { AthleteResultListVD } from "../viewdata/athlete-result-list.vd";
import { EventListVD } from "../viewdata/event-list.vd";
import { NewsListVD } from "../viewdata/news-list.vd";
import { WCupSeasonVD } from "../viewdata/wcup-season.vd";
import { RecordAreasListVD } from "../viewdata/records-area-list.vd";
import { RecordHistoryListVD } from "../viewdata/record-history-list.vd";
import { SeriesView } from "../viewdata/series-list.vd";
import { SeriesProView } from "../viewdata/series-pro.vd";
import { CompetitionDisciplinesVD } from '../viewdata/competitions-discipline-list.vd';
import { CompetitionLevelsVD } from "../viewdata/competitions-level-list.vd";

// Locators.
import { CategoryListLocator } from "../locators/category-list.locator";
import { CountryNamesListLocator } from "../locators/country-names-list.locator";

// Interfaces.
import { LocatorInterface } from "../interfaces/locator.interface";
import { ViewDataInterface } from "../interfaces/view-data.interface";

// Classes.
import { WCupSeasonItem } from "../fetching/types/wcup-season.item";

// Providers.
import { WABaseService } from "./wa-base.service";
import { LoggerService } from "../logger/logger-service";
import { WaHelperService } from "./wa-helper-service";
import { MemStorageService } from "./mem-storage.service";
import { DataFetchService } from "./data-fetch-service";
import { DatabaseService } from "./database-service";
import { AthletesDatabasePouchDbService } from "../wa-services/athletes-database.service";
import { FactoryService } from "./factory-service";
import { EnvironmentService } from "./environment-service";
import { SeriesStagesItem } from "../fetching/types/series-stages.info.item";
import { SeriesStageView } from "../viewdata/series-stage.list";
import { SeriesAmateurRankingView } from "../viewdata/series-amateurs-rankings.list";
import { SeriesTeamRankingView } from "../viewdata/series-team-ranking.list";
import { WorldRankingCategoryListLocator } from "../locators/world-ranking-category-list.locator";
import { SeriesAmateursRankingsItems, DivisionCategoryListVD } from 'world-archery-lib';


@Injectable({
  providedIn: 'root'
})
export class ViewDataService extends WABaseService {
  protected viewCount: number = 0;

  protected seasonChangedStream: ReplaySubject<WCupSeasonItem>;

  constructor(
    protected logger: LoggerService,
    protected helper: WaHelperService,
    protected storage: MemStorageService,
    protected fetchSrvc: DataFetchService,
    protected db: DatabaseService,
    protected athDb: AthletesDatabasePouchDbService,
    protected factory: FactoryService,
    protected env: EnvironmentService
  ) {
    super(logger, env);

    this.seasonChangedStream = new ReplaySubject<WCupSeasonItem>(1);
    this.seasonChangedStream.next(null);
  }

  public getInfo() {
    return {
      count: this.viewCount
    };
  }

  public get(locator: LocatorInterface): ViewDataInterface {
    let viewData: ViewDataInterface;
    let key = locator.getId();

    if (!this.storage.keyExists(key)) {
      // Create ViewData.
      this.createViewData(locator).init();
      this.viewCount++;
    }

    viewData = this.storage.get(key);

    this.debug(`Returning view «${locator.getId()}»`);
    return viewData;
  }

  protected createViewData(locator: LocatorInterface): ViewDataInterface {
    let viewData: ViewDataInterface;
    switch (locator.getType()) {
      case "categoryList":
        viewData = this.createCategoryListVD(locator);
        break;

      case "countryNamesList":
        viewData = this.createCountryNamesListVD(locator);
        break;

      case "recordList":
        viewData = this.createRecordListVD(locator);
        break;

      case "wCupRankingCategoryList":
        viewData = this.createWCupRankingCategoriesListVD(locator);
        break;

      case "wCupRankingList":
        viewData = this.createWCupRankingListVD(locator);
        break;

      case "worldRankingCategoryList":
        viewData = this.createWorldRankingCategoriesListVD(locator);
        break;

      case "worldRankingList":
        viewData = this.createWorldRankingListVD(locator);
        break;

      case "completeAthleteList":
        viewData = this.createCompleteAthleteListVD(locator);
        break;

      case "athlete":
        viewData = this.createAthleteVD(locator);
        break;

      case "eventList":
        viewData = this.createEventListVD(locator);
        break;

      case "news":
        viewData = this.createNewsListVD(locator);
        break;

      case "wCupSeason":
        viewData = this.createWCupSeasonVD(locator);
        break;

      case "athleteResultList":
        viewData = this.createAthleteResultListVD(locator);
        break;
      case "seriesStageLocator":
        viewData = this.createSeriesStateResultListVD(locator);
        break;
      case "seriesAmateursLocator":
        viewData = this.createSeriesAmateursLocator(locator);
        break;
      case "seriesTeamsRankings":
        viewData = this.createSeriesTeamsRankingsLocator(locator);
        break;
      case "recordsAreas":
        viewData = this.createRecordAreasList(locator);
        break;
      case "recordHistoryList":
        viewData = this.createRecordHistoryList(locator);
        break;
      case "seriesList":
        viewData = this.createSeriesList(locator);
        break;
      case "seriesProLocator":
        viewData = this.createProList(locator);
        break;
      case "competitionDisciplinesList":
        viewData = this.createdCompetitionDisciplines(locator);
        break;
      case "competitionLevelsList":
        viewData = this.createdCompetitionLevels(locator);
        break;
      case "divisionCategory":
        viewData = this.createDvisionCategoryListVD(locator);
        break;
      default:
        this.error("Unkonwn locator type");
    }

    this.storage.put(locator.getId(), viewData);
    viewData.connectDependencies();
    this.log(
      `Created view «${locator.getId()}» with given locator`,
      viewData,
      locator
    );
    return viewData;
  }

  protected createCategoryListVD(locator: LocatorInterface): CategoryListVD {
    let viewData = new CategoryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    viewData.configureView();
    return viewData;
  }

  protected createCountryNamesListVD(locator: LocatorInterface): CountryListVD {
    let viewData = new CountryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    viewData.configureView();
    return viewData;
  }

  protected createRecordListVD(locator: LocatorInterface): RecordListVD {
    let viewData = new RecordListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createWCupRankingCategoriesListVD(
    locator: LocatorInterface
  ): WCupRankingCategoryListVD {
    let viewData = new WCupRankingCategoryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);
    return viewData;
  }

  protected createWCupRankingListVD(
    locator: LocatorInterface
  ): WCupRankingListVD {
    let viewData = new WCupRankingListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createWorldRankingCategoriesListVD(
    locator: LocatorInterface
  ): WorldRankingCategoryListVD {
    let viewData = new WorldRankingCategoryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);

    return viewData;
  }

  protected createWorldRankingListVD(
    locator: LocatorInterface
  ): WorldRankingListVD {
    let viewData = new WorldRankingListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createCompleteAthleteListVD(
    locator: LocatorInterface
  ): AthleteListVD {
    let viewData = new AthleteListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.athDb,
      this.factory,
      this.helper,
      locator
    );

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createAthleteVD(locator: LocatorInterface): AthleteVD {
    let viewData = new AthleteVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createEventListVD(locator: LocatorInterface): EventListVD {
    let viewData = new EventListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let categoryListLocator = new CategoryListLocator();
    let dependendatCategoryListVD = this.get(categoryListLocator);

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCategoryListVD);
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  protected createNewsListVD(locator: LocatorInterface): NewsListVD {
    let viewData = new NewsListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      locator
    );
    viewData.configureView();
    return viewData;
  }

  protected createWCupSeasonVD(locator: LocatorInterface): WCupSeasonVD {
    let viewData = new WCupSeasonVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      locator
    );
    viewData.configureView();

    // Link season changed event with this service seasion changed stream.
    viewData.seasonChangedStream.subscribe((season: WCupSeasonItem) =>
      this.seasonChangedStream.next(season)
    );

    return viewData;
  }

  protected createAthleteResultListVD(
    locator: LocatorInterface
  ): AthleteResultListVD {
    let viewData = new AthleteResultListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    let countryListLocator = new CountryNamesListLocator();
    let dependendatCountryListVD = this.get(countryListLocator);

    viewData.configureView();
    viewData.addDependency(dependendatCountryListVD);
    return viewData;
  }

  public getSeasonChangeStream(): Observable<WCupSeasonItem> {
    return this.seasonChangedStream.asObservable();
  }
  protected createSeriesStateResultListVD(locator: LocatorInterface) {
    const viewData = new SeriesStageView(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createSeriesAmateursLocator(locator: LocatorInterface) {
    const viewData = new SeriesAmateurRankingView(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createSeriesTeamsRankingsLocator(locator: LocatorInterface) {
    const viewData = new SeriesTeamRankingView(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createRecordAreasList(locator: LocatorInterface) {
    const viewData = new RecordAreasListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      locator,
      this.factory,
      this.helper
    );
    return viewData;
  }
  protected createRecordHistoryList(locator: LocatorInterface) {
    const viewData = new RecordHistoryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createSeriesList(locator: LocatorInterface) {
    const viewData = new SeriesView(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }

  protected createProList(locator: LocatorInterface): any {
    const viewData = new SeriesProView(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createdCompetitionDisciplines(locator: LocatorInterface): any {
    const viewData = new CompetitionDisciplinesVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createdCompetitionLevels(locator: LocatorInterface): any {
    const viewData = new CompetitionLevelsVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );
    return viewData;
  }
  protected createDvisionCategoryListVD(
    locator: LocatorInterface
  ): DivisionCategoryListVD {
    let viewData = new DivisionCategoryListVD(
      this.logger,
      this.fetchSrvc,
      this.db,
      this.factory,
      this.helper,
      locator
    );

    viewData.configureView();
    return viewData;
  }

}
